import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * Full-bleed magazine spread with cool-tone panel.
 * Image fades softly into the panel color for a seamless blend.
 * Section blends from previous color to next via top/bottom gradients.
 */
export default function EditorialImage({
  src,
  alt = "",
  eyebrow,
  quote,
  caption,
  chapter,
  panel = "#1a1f24",
  fromColor = "#0f1216",
  toColor = "#0f1216",
  reverse = false,
  testId,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  const fadeClass = reverse ? "fade-left" : "fade-right";

  return (
    <section
      ref={ref}
      data-testid={testId}
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: panel,
        backgroundImage: `linear-gradient(to bottom, ${fromColor} 0%, ${panel} 12%, ${panel} 88%, ${toColor} 100%)`,
      }}
    >
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 items-stretch ${
          reverse ? "lg:[direction:rtl]" : ""
        }`}
      >
        {/* Image side — fades into the panel */}
        <div className="relative aspect-square lg:aspect-auto overflow-hidden [direction:ltr]">
          <motion.img
            src={src}
            alt={alt}
            style={{ y }}
            className={`w-full h-full object-cover will-change-transform hidden lg:block ${fadeClass}`}
            draggable={false}
            loading="lazy"
          />
          {/* Mobile: no mask fade (would look odd on a stacked layout) */}
          <img
            src={src}
            alt=""
            aria-hidden
            className="w-full h-full object-cover lg:hidden"
            draggable={false}
            loading="lazy"
          />
        </div>

        {/* Copy panel */}
        <div
          className="relative flex flex-col justify-center px-8 md:px-14 lg:px-20 py-16 md:py-24 lg:py-0 min-h-[560px] lg:min-h-0 [direction:ltr] grain"
        >
          {chapter && (
            <p className="font-serif text-[#e8ecef]/35 text-6xl md:text-7xl leading-none tracking-tight mb-10">
              {chapter}
            </p>
          )}
          {eyebrow && (
            <p className="text-[#e8ecef]/75 text-[10px] md:text-xs tracking-[0.42em] uppercase mb-8">
              {eyebrow}
            </p>
          )}
          {quote && (
            <p className="font-serif text-[#e8ecef] text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight font-light max-w-[16ch]">
              {quote}
            </p>
          )}
          {caption && (
            <p className="mt-10 text-[#e8ecef]/70 text-sm md:text-base leading-relaxed font-light max-w-md">
              {caption}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
